import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LastSubject } from "../core/LastSubject";
import { BehaviorSubject, Observable } from "rxjs";
import { API_CMS_ENDPOINT, API_ENDPOINT } from '../core/DI';
import { ParamMap, Params } from '@angular/router';
import { Helper } from '../shared/helper';
import { FilterService } from '../shared/filter.service';
import { SingleGenericStringValueIdCategoryFilter, CategoryFiltersRequestDto } from '../core/models/filter-backend';
import { Constants } from '../core/Constants';
import { PackageBuilderService } from '../package-builder/package-builder.service';
import { MakeYearModel } from '../core/models/make-year-model.model';
import { HardwareService } from '../shared/hardware.service';
import { WheelService } from '../shared/wheel.service';

@Injectable()
export class FileService {
  apiEndpoint: string;
  apiCmsEndpoint: string;
  public pbExportToExcelSubject = new LastSubject<any>();

  constructor(@Inject(API_ENDPOINT) apiEndpoint: string,
    @Inject(API_CMS_ENDPOINT) apiCmsEndpoint: string,
    private http: HttpClient,
    private filterService: FilterService,
    private hardwareService: HardwareService,
    private wheelService: WheelService,
    private packageBuilderService: PackageBuilderService) {

    this.apiEndpoint = apiEndpoint;
    this.apiCmsEndpoint = apiCmsEndpoint;

  }

  get onPBExportToExcel(): Observable<object> {
    return this.pbExportToExcelSubject.asObservable();
  }

  exportWheelsToExcel(params: ParamMap, productBySpecs: boolean, showPromotions: boolean = false): any {
    const makeYearModel = Helper.extractMakeYearModelFromRouteParam(params);
    const categoryFiltersDto = this.filterService.convertCategoryFiltersDtoFromURL(params);

    let stagger: string = null;
    if ((<any>categoryFiltersDto).wheelstagger) {
      stagger = (<SingleGenericStringValueIdCategoryFilter>(<any>categoryFiltersDto).wheelstagger).value.id;
      delete (<any>categoryFiltersDto).wheelstagger;
    }

    let requestParam = {};
    let api = `${this.apiCmsEndpoint}/angular/packagebuilder/wheels/excel/download`;
    requestParam = {
      baseFilter: makeYearModel,
      secondaryFilter: categoryFiltersDto,
      loadSecondaryFilters: false,
      stagger: stagger
    };

    if (productBySpecs) {
      const baseFilter = this.wheelService.getCategoryFiltersFromRoute(params);

      api = `${this.apiCmsEndpoint}/angular/wheelsbyspecs/excel/download?showPromotions=${showPromotions}`;
      requestParam = {
        baseFilter: baseFilter,
        secondaryFilter: categoryFiltersDto,
        loadSecondaryFilters: false
      };
    }

    return this.http.post<any>(api, requestParam, { observe: 'response', responseType: 'blob' as 'json' });
  }

  packageBuilderExportTiresToExcel(params: ParamMap, showPromotions: boolean = false): any {
    let requestParam = {};
    const api = `${this.apiCmsEndpoint}/angular/packagebuilder/tires/excel/download?showPromotions=${showPromotions}`;
    const makeYearModel = Helper.extractMakeYearModelFromRouteParam(params);
    const categoryFiltersDto = this.filterService.convertCategoryFiltersDtoFromURL(params);
    const wheelIds = this.packageBuilderService.getWheelIds();

    let frontOffset = null;
    let rearOffset = null;
    if (wheelIds.front.startsWith(Constants.CUSTOMER_SIZE)) {
      frontOffset = this.packageBuilderService.getFrontWheelOffset();
      rearOffset = this.packageBuilderService.getRearWheelOffset();
    }

    let stagger: string = null;
    if ((<any>categoryFiltersDto).wheelstagger) {
      stagger = (<SingleGenericStringValueIdCategoryFilter>(<any>categoryFiltersDto).wheelstagger).value.id;
      delete (<any>categoryFiltersDto).wheelstagger;
    }

    requestParam = {
      baseFilter: makeYearModel,
      secondaryFilter: categoryFiltersDto,
      loadSecondaryFilters: false,
      frontWheelId: wheelIds.front,
      rearWheelId: wheelIds.rear,
      stagger: stagger,
      frontOffset: frontOffset,
      rearOffset: rearOffset,
      frontWheelQty: wheelIds.frontQty,
      rearWheelQty: wheelIds.rearQty
    };

    return this.http.post<any>(api, requestParam, { observe: 'response', responseType: 'blob' as 'json' });
  }

  packageBuilderExportHardwareToExcel(params: Params, catId: number, frontWheelId: string, rearWheelId: string = ''): any {

    const mym = new MakeYearModel();
    mym.make = params[Constants.MAKE_ROUTE_PARAM];
    mym.year = params[Constants.YEAR_ROUTE_PARAM];
    mym.model = params[Constants.MODEL_ROUTE_PARAM];
    mym.submodel = params[Constants.SUBMODEL_ROUTE_PARAM];
    mym.option = params[Constants.OPTIONS_ROUTE_PARAM];
    mym.isSummerSeason = params[Constants.SEASON_ROUTE_PARAM] === 'summer';
    mym.defaultAggressive = params[Constants.DEFAULT_AGGRESIVE_ROUTE_PARAM] === "1";

    const concept = params[Constants.CONCEPT_VEHICLE_ROUTE_PARAM];
    mym.isConcept = concept !== null && concept !== undefined && concept === '1' ? true : false;

    const api = `${this.apiCmsEndpoint}/angular/packagebuilder/hardware/excel/download`;
    const requestParam = {
      makeYearModel: mym,
      categoryId: catId,
      frontWheelId: frontWheelId,
      rearWheelId: rearWheelId
    };

    return this.http.post<any>(api, requestParam, { observe: 'response', responseType: 'blob' as 'json' });
  }

  downloadAccessoriesToExcel(params: ParamMap, showPromotions: boolean = false): any {
    const secondaryFilter = this.filterService.convertCategoryFiltersDtoFromURL(params);
    const categoryFilter = this.hardwareService.getCategoryFiltersFromRoute(params);
    const api = `${this.apiCmsEndpoint}/angular/accessories/excel/download?showPromotions=${showPromotions}`;

    const request = {
      baseFilter: categoryFilter,
      secondaryFilter: secondaryFilter,
      loadSecondaryFilters: false
    };

    return this.http.post<any>(api, request, { observe: 'response', responseType: 'blob' as 'json' });
  }

  downloadSearchResultToExcel(searchKeyword: string): any {
    const api = `${this.apiCmsEndpoint}/angular/search/result/download?searchKeyword=${searchKeyword}`;
    return this.http.get(api, { observe: 'response', responseType: 'blob' });
  }

  downloadResponseToExcel(tempFilename: string, response: any, useTempFilename: boolean = false) {
    const _body = response.body;
    const _type = response.headers.get('Content-Type');
    const _contentDisposition = response.headers.get('content-disposition');

    let _filename: string = tempFilename;
    if (!useTempFilename) {
      let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      var matches = filenameRegex.exec(_contentDisposition);
      if (matches != null && matches[1])
        _filename = decodeURIComponent(matches[1].replace(/['"]/g, ''));
    }

    let binaryData = [];
    binaryData.push(_body);
    let downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: _type }));
    downloadLink.setAttribute('download', _filename);
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }
}
